import {
  DRIVER_COMPLIANCE_STATUS_COLORS,
  DRIVER_COMPLIANCE_STATUS_LABELS,
  DRIVER_PROFILE_DEFAULTS,
} from "../constants/driver-profile";

/**
 * Parse endorsements from form value or stored meta string.
 *
 * @param {string[]|string|undefined} value
 * @returns {string[]}
 */
export function parseEndorsements(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim().toUpperCase()).filter(Boolean);
  }
  if (!value) return [];
  return String(value)
    .split(",")
    .map((entry) => entry.trim().toUpperCase())
    .filter(Boolean);
}

/**
 * Merge flat driver form values into API meta payload.
 *
 * @param {Object} form
 * @returns {Object<string, string|boolean>}
 */
export function buildDriverMetaPayload(form = {}) {
  const endorsements = parseEndorsements(form.endorsements);

  return {
    licenseNumber: form.licenseNumber || form.license || "",
    licenseState: form.licenseState || "",
    licenseType: form.licenseType || "A",
    licenseExpiration: form.licenseExpiration || "",
    endorsements: endorsements.join(","),
    medicalCertExpiration: form.medicalCertExpiration || "",
    hazmatCertified: !!form.hazmatCertified,
    hazmatCertExpiration: form.hazmatCertExpiration || "",
    twicCertified: !!form.twicCertified,
    twicExpiration: form.twicExpiration || "",
    yearsOfExperience: form.yearsOfExperience || "",
    payType: form.payType || "company_driver",
    employmentStatus: form.employmentStatus || "active",
    homeTerminal: form.homeTerminal || "",
    lastDrugTestDate: form.lastDrugTestDate || "",
    nextDrugTestDue: form.nextDrugTestDue || "",
    lastMvrDate: form.lastMvrDate || "",
    emergencyContactName: form.emergencyContactName || "",
    emergencyContactPhone: form.emergencyContactPhone || "",
    hourlyRate: form.hourlyRate || "",
    mileageRate: form.mileageRate || "",
    emptyRate: form.emptyRate || "",
    license: form.licenseNumber || form.license || "",
  };
}

/**
 * Hydrate flat form fields from a driver user record.
 *
 * @param {Object} driver
 * @returns {Object}
 */
export function hydrateDriverFormFromRecord(driver = {}) {
  const profile = driver.driverProfile || {};
  const meta = driver.meta || {};

  const readMeta = (key, fallback = "") =>
    profile[key] ?? meta[key] ?? fallback;

  return {
    ...DRIVER_PROFILE_DEFAULTS,
    licenseNumber: readMeta("licenseNumber", meta.license || ""),
    license: readMeta("licenseNumber", meta.license || ""),
    licenseState: readMeta("licenseState"),
    licenseType: readMeta("licenseType", "A"),
    licenseExpiration: readMeta("licenseExpiration"),
    endorsements: parseEndorsements(
      profile.endorsements ?? meta.endorsements ?? []
    ),
    medicalCertExpiration: readMeta("medicalCertExpiration"),
    hazmatCertified:
      profile.hazmatCertified === true ||
      String(meta.hazmatCertified).toLowerCase() === "true",
    hazmatCertExpiration: readMeta("hazmatCertExpiration"),
    twicCertified:
      profile.twicCertified === true ||
      String(meta.twicCertified).toLowerCase() === "true",
    twicExpiration: readMeta("twicExpiration"),
    yearsOfExperience: readMeta("yearsOfExperience"),
    payType: readMeta("payType", "company_driver"),
    employmentStatus: readMeta("employmentStatus", "active"),
    homeTerminal: readMeta("homeTerminal"),
    lastDrugTestDate: readMeta("lastDrugTestDate"),
    nextDrugTestDue: readMeta("nextDrugTestDue"),
    lastMvrDate: readMeta("lastMvrDate"),
    emergencyContactName: readMeta("emergencyContactName"),
    emergencyContactPhone: readMeta("emergencyContactPhone"),
    hourlyRate: readMeta("hourlyRate"),
    mileageRate: readMeta("mileageRate"),
    emptyRate: readMeta("emptyRate"),
  };
}

/**
 * Resolve compliance badge props for list/select surfaces.
 *
 * @param {Object} driver
 * @returns {{status:string,label:string,color:string,canAssign:boolean}}
 */
export function getDriverComplianceBadge(driver = {}) {
  const compliance = driver.driverCompliance || {};
  const status = compliance.status || "missing";

  return {
    status,
    label: DRIVER_COMPLIANCE_STATUS_LABELS[status] || "Incomplete",
    color: DRIVER_COMPLIANCE_STATUS_COLORS[status] || "gray",
    canAssign: compliance.canAssign !== false,
  };
}

/**
 * Format endorsements for display.
 *
 * @param {string[]|string|undefined} value
 * @returns {string}
 */
export function formatEndorsements(value) {
  const endorsements = parseEndorsements(value);
  return endorsements.length ? endorsements.join(", ") : "None";
}
